import { SEO } from "../components/SEO";
import { Hero } from "../components/Hero";
import { IndustryBelt } from "../components/IndustryBelt";
import { Section, SectionHeader } from "../components/Section";
import { FeatureCard } from "../components/Card";
import { CTASection } from "../components/CTASection";
import { Link } from "react-router-dom";
import { INDUSTRIES, INDUSTRY_CATEGORIES } from "../lib/industries";
import { DOMAINS } from "../lib/domains";
import { useTranslation } from "../lib/i18n";

export default function HomePage() {
  const { t } = useTranslation();

  const CORE_CAPABILITIES = [
    { title: t("home.cap1.title"), description: t("home.cap1.desc") },
    { title: t("home.cap2.title"), description: t("home.cap2.desc") },
    { title: t("home.cap3.title"), description: t("home.cap3.desc") },
    { title: t("home.cap4.title"), description: t("home.cap4.desc") },
    { title: t("home.cap5.title"), description: t("home.cap5.desc") },
    { title: t("home.cap6.title"), description: t("home.cap6.desc") },
  ];

  const WHY_SENTINEL = [
    { title: t("home.why1.title"), description: t("home.why1.desc") },
    { title: t("home.why2.title"), description: t("home.why2.desc") },
    { title: t("home.why3.title"), description: t("home.why3.desc") },
    { title: t("home.why4.title"), description: t("home.why4.desc") },
    { title: t("home.why5.title"), description: t("home.why5.desc") },
    { title: t("home.why6.title"), description: t("home.why6.desc") },
  ];

  return (
    <>
      <SEO
        title={t("home.hero.title")}
        description={t("home.hero.subtitle")}
        image="https://images.pexels.com/photos/265667/pexels-photo-265667.jpeg?auto=compress&cs=tinysrgb&w=1200"
      />
      <Hero
        eyebrow={t("home.hero.eyebrow")}
        title={t("home.hero.title")}
        subtitle={t("home.hero.subtitle")}
        primaryCTA={{ label: t("home.hero.primary"), to: "/register" }}
        secondaryCTA={{ label: t("home.hero.secondary"), to: "/architecture" }}
      />

      <IndustryBelt />

      {/* What Sentinel Is */}
      <Section>
        <SectionHeader
          eyebrow={t("home.what.eyebrow")}
          title={t("home.what.title")}
          description={t("home.what.description")}
        />
        <div className="grid grid-3">
          {CORE_CAPABILITIES.map((cap) => (
            <FeatureCard key={cap.title} title={cap.title} description={cap.description} />
          ))}
        </div>
      </Section>

      {/* Core Capabilities list */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow={t("home.capabilities.eyebrow")}
          title={t("home.capabilities.title")}
        />
        <div className="grid grid-2">
          {CORE_CAPABILITIES.map((cap, i) => (
            <div key={i} style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
              <div
                style={{
                  minWidth: "32px",
                  height: "32px",
                  borderRadius: "var(--radius-md)",
                  background: "var(--accent-500)",
                  color: "var(--neutral-950)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  fontSize: "0.875rem",
                }}
              >
                {i + 1}
              </div>
              <div>
                <h3 style={{ fontSize: "1.0625rem", marginBottom: "0.375rem" }}>{cap.title}</h3>
                <p className="text-muted" style={{ fontSize: "0.9375rem" }}>{cap.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Why Sentinel Exists */}
      <Section variant="dark">
        <SectionHeader
          eyebrow={t("home.why.eyebrow")}
          title={t("home.why.title")}
          dark
        />
        <div className="grid grid-3">
          {WHY_SENTINEL.map((item) => (
            <FeatureCard key={item.title} title={item.title} description={item.description} dark />
          ))}
        </div>
      </Section>

      {/* Video Introduction */}
      <Section variant="dark">
        <SectionHeader
          eyebrow={t("home.video.eyebrow")}
          title={t("home.video.title")}
          description={t("home.video.description")}
          dark
        />
        <div
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            position: "relative",
            borderRadius: "var(--radius-xl)",
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.1)",
            background: "rgba(255,255,255,0.03)",
          }}
        >
          <div
            style={{
              aspectRatio: "16 / 9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, rgba(10,22,40,0.6), rgba(10,22,40,0.9))",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "72px",
                  height: "72px",
                  borderRadius: "50%",
                  background: "var(--accent-500)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 1rem",
                  cursor: "pointer",
                  transition: "transform var(--transition)",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.1)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              >
                <span style={{ fontSize: "1.75rem", color: "var(--neutral-950)", marginLeft: "0.25rem" }}>
                  &#9654;
                </span>
              </div>
              <p className="lead-dark" style={{ fontSize: "1rem" }}>
                {t("home.video.label")}
              </p>
              <p style={{ color: "var(--text-muted-on-dark)", fontSize: "0.8125rem" }}>
                {t("home.video.meta")}
              </p>
            </div>
          </div>
        </div>
        <div style={{ textAlign: "center", marginTop: "var(--space-3)" }}>
          <Link
            to="/downloads"
            style={{
              fontSize: "0.875rem",
              color: "var(--accent-400)",
              textDecoration: "underline",
            }}
          >
            {t("home.video.downloadScript")}
          </Link>
        </div>
      </Section>

      {/* Industries overview */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow={t("home.industries.eyebrow")}
          title={t("home.industries.title")}
          description={t("home.industries.description")}
        />
        {INDUSTRY_CATEGORIES.map((category) => (
          <div key={category} style={{ marginBottom: "var(--space-4)" }}>
            <h3 style={{ fontSize: "0.875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--accent-700)", marginBottom: "0.75rem" }}>
              {category}
            </h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {INDUSTRIES.filter((i) => i.category === category).map((industry) => (
                <Link
                  key={industry.slug}
                  to={`/industries/${industry.slug}`}
                  className="badge"
                  style={{
                    background: "var(--surface)",
                    color: "var(--text)",
                    border: "1px solid var(--border)",
                    padding: "0.5rem 0.875rem",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    transition: "all var(--transition-fast)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--accent-400)";
                    e.currentTarget.style.color = "var(--accent-600)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "var(--border)";
                    e.currentTarget.style.color = "var(--text)";
                  }}
                >
                  {industry.name}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </Section>

      {/* Domains overview */}
      <Section>
        <SectionHeader
          eyebrow={t("home.domains.eyebrow")}
          title={t("home.domains.title")}
          description={t("home.domains.description")}
        />
        <div className="grid grid-3">
          {[...DOMAINS].sort((a, b) => a.name.localeCompare(b.name)).map((domain) => (
            <Link key={domain.slug} to={`/domains/${domain.slug}`} style={{ textDecoration: "none" }}>
              <div
                className="card card-hover"
                style={{
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: "var(--radius-lg)",
                  minHeight: "180px",
                  padding: 0,
                  border: "none",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `url(${domain.backgroundImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(180deg, rgba(10,22,40,0.25) 0%, rgba(10,22,40,0.88) 75%)",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    height: "100%",
                    minHeight: "180px",
                    padding: "1.25rem",
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "var(--radius-md)",
                      background: "rgba(59,130,246,0.9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: "0.95rem",
                      marginBottom: "0.5rem",
                      flexShrink: 0,
                    }}
                  >
                    {domain.name[0]}
                  </div>
                  <h3 style={{ fontSize: "1.05rem", color: "#fff", marginBottom: "0.25rem" }}>{domain.name}</h3>
                  <p style={{ fontSize: "0.8125rem", color: "rgba(191,219,254,0.9)", margin: 0 }}>{domain.tagline}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <CTASection
        title={t("home.cta.title")}
        description={t("home.cta.description")}
        primaryLabel={t("cta.deploy")}
        primaryTo="/register"
        secondaryLabel={t("home.cta.secondary")}
        secondaryTo="/pricing"
      />
    </>
  );
}
